import {
	CustomRxEvent,
	CustomRxEventType,
	RxCompleteData,
	RxStatusData,
	RxUIReadyData,
	TokenRefreshRequestData
} from '../models/lab-broker-event.model';
import { CachedToken } from './custom-rx-token.service';
import { catchError, EMPTY, filter, forkJoin, merge, Observable, ReplaySubject, share, switchMap, take, takeUntil, tap, timer } from 'rxjs';

/**
 * Callbacks that DentiraCommunicationSession invokes when iframe events arrive.
 * The facade implements this interface so the session stays decoupled from
 * store, logger, and token logic.
 */
export interface DentiraCommunicationHandlers {
	/**
	 * Called once when RxUIReady is received and the iframe element + token are ready.
	 * The session resolves contentWindow from the iframe element and passes it here
	 * so the facade can wire up LabBrokerCommunicationService.setIframeWindow.
	 */
	onRxUIReady(data: RxUIReadyData, token: CachedToken, contentWindow: Window): void;
	/** Called when RxComplete is received. */
	onRxComplete(data: RxCompleteData): void;
	/** Called when RxStatus is received. */
	onRxStatus(data: RxStatusData): void;
	/**
	 * Called when TokenRefreshRequest is received.
	 * Must return an Observable that completes after the refresh attempt finishes
	 * (success or handled error). The session subscribes to it via switchMap.
	 */
	onTokenRefreshRequest(data: TokenRefreshRequestData): Observable<void>;
	/** Called when the RxUIReady timeout fires before the event arrives. */
	onTimeout(): void;
	/** Called on unexpected stream errors or unhandled exceptions inside event processing. */
	onError(error: unknown): void;
}

/**
 * Encapsulates the reactive event-loop for a single Dentira iframe session.
 *
 * Lifetime: one instance per `initCommunication` call. The instance is not
 * reused — create a new one each time the iframe URL is resolved.
 *
 * Architecture:
 *  - events$ — shared multicasted stream from the communication service
 *  - Branch 1 (waitForReady$)    — waits for RxUIReady, publishes to rxUIReady$ gate
 *  - Branch 2 (readyTimeoutGuard$) — fires onTimeout() if RxUIReady is late,
 *                                    cancelled by rxUIReady$ so it never fires after ready
 *  - Branch 3 (handleEvents$)    — unblocked by rxUIReady$, processes all subsequent events
 *
 * The three branches are merged so events$ stays alive regardless of which branch completes.
 */
export class DentiraCommunicationSession {
	/** Gate subject: emits once when RxUIReady is received (or late-received). */
	private readonly rxUIReady$ = new ReplaySubject<void>(1);

	/** Shared multicasted event stream — one real subscription for all branches. */
	private readonly events$: Observable<MessageEvent<CustomRxEvent<unknown>>>;

	constructor(
		rawEvents$: Observable<MessageEvent<CustomRxEvent<unknown>>>,
		private readonly iframeNativeElement$: Observable<HTMLIFrameElement>,
		private readonly token$: Observable<CachedToken>,
		private readonly handlers: DentiraCommunicationHandlers,
		private readonly readyTimeoutMs: number
	) {
		this.events$ = rawEvents$.pipe(share());
	}

	/**
	 * Starts the session. Subscribe to the returned Observable to activate all
	 * three branches. The Observable never emits values — it only errors or
	 * completes when the stream is torn down.
	 */
	connect(): Observable<never> {
		return merge(this.waitForReady$(), this.readyTimeoutGuard$(), this.handleEvents$()).pipe(
			catchError((err: unknown) => {
				this.handlers.onError(err);

				return EMPTY;
			})
		);
	}

	// ─── Branch 1 ────────────────────────────────────────────────────────────────

	/**
	 * Filters the stream down to the first RxUIReady event.
	 * On arrival: publishes to rxUIReady$ gate (unblocking branch 3) and wires
	 * up the iframe window + calls onRxUIReady once the element and token resolve.
	 */
	private waitForReady$(): Observable<never> {
		return this.events$.pipe(
			filter(event => this.isEventOfType(event, CustomRxEventType.RxUIReady)),
			tap(() => this.rxUIReady$.next()),
			take(1),
			switchMap(event =>
				forkJoin([this.iframeNativeElement$, this.token$]).pipe(
					take(1),
					tap(([iframeNativeElement, token]) => {
						const contentWindow = iframeNativeElement?.contentWindow ?? window;

						this.handlers.onRxUIReady(event.data.Data as RxUIReadyData, token, contentWindow);
					}),
					switchMap(() => EMPTY)
				)
			)
		);
	}

	// ─── Branch 2 ────────────────────────────────────────────────────────────────

	/**
	 * Starts a timer for readyTimeoutMs. If it fires before rxUIReady$ emits,
	 * calls onTimeout(). Cancelled automatically once RxUIReady arrives so
	 * both timeout and ready never fire for the same session.
	 */
	private readyTimeoutGuard$(): Observable<never> {
		return timer(this.readyTimeoutMs).pipe(
			takeUntil(this.rxUIReady$),
			tap(() => this.handlers.onTimeout()),
			switchMap(() => EMPTY)
		);
	}

	// ─── Branch 3 ────────────────────────────────────────────────────────────────

	/**
	 * Waits for the rxUIReady$ gate, then processes all non-RxUIReady events
	 * for the lifetime of the session.
	 */
	private handleEvents$(): Observable<never> {
		return this.rxUIReady$.pipe(
			take(1),
			switchMap(() =>
				this.events$.pipe(
					filter(event => !this.isEventOfType(event, CustomRxEventType.RxUIReady)),
					switchMap(event => this.dispatchEvent(event))
				)
			)
		) as Observable<never>;
	}

	// ─── Event dispatch ──────────────────────────────────────────────────────────

	private dispatchEvent(event: MessageEvent<CustomRxEvent<unknown>>): Observable<unknown> {
		try {
			const message = event.data;

			switch (message.EventHeader.Type) {
				case CustomRxEventType.RxComplete:
					this.handlers.onRxComplete(message.Data as RxCompleteData);

					return EMPTY;

				case CustomRxEventType.TokenRefreshRequest:
					return this.handlers.onTokenRefreshRequest(message.Data as TokenRefreshRequestData);

				case CustomRxEventType.RxStatus:
					this.handlers.onRxStatus(message.Data as RxStatusData);

					return EMPTY;

				default:
					return EMPTY;
			}
		} catch (error: unknown) {
			this.handlers.onError(error);

			return EMPTY;
		}
	}

	// ─── Helpers ─────────────────────────────────────────────────────────────────

	private isEventOfType(event: MessageEvent<CustomRxEvent<unknown>>, type: CustomRxEventType): boolean {
		try {
			return event.data.EventHeader.Type === type;
		} catch {
			return false;
		}
	}
}
